/**
 * 取引先(共通マスタ)のシート用書き出しと一括取り込み(2026-10-23 決定 K5・全体設計 3章)。
 *
 *  - 正本は Gate のマスタ。シートは写し(見る・共有する用)。第1版は画面のボタンで書き出す(自動の写しは後で)
 *  - 一括取り込みは最初の大量登録用。書き出しと同じ列で読み、重複・形式を確かめてから「新規」の行だけ登録する
 *  - 既存の取引先の上書きはしない(直すのはマスタ画面から)
 *
 * 具体例は tests/unit/partnerCsv.test.ts
 */
import type { CsvTable } from '@core/cash/csv';
import type { DayOrEnd, HolidayShift, PartnerKind, PaymentTerms, Vendor } from '@core/lib/types';
import {
  HOLIDAY_SHIFT_OPTIONS,
  MONTH_OFFSET_OPTIONS,
  PARTNER_KIND_OPTIONS,
  kindsOf,
  nameKey,
  normalizeInvoiceRegNo,
  validateTerms,
} from './terms';

/** 登録・更新に使う入力(画面・一括取り込み共通) */
export type PartnerInput = {
  name: string;
  legalName: string;
  kinds: PartnerKind[];
  invoiceAddressee: string;
  postalCode: string;
  address: string;
  invoiceRegNo: string;
  bankInfo: string;
  payTerms: PaymentTerms | null;
  collectTerms: PaymentTerms | null;
  note: string;
};

export const PARTNER_CSV_HEADERS = [
  '管理番号',
  '名称',
  '正式名称',
  '区分',
  '請求書の宛名',
  '郵便番号',
  '住所',
  'インボイス登録番号',
  '振込先',
  '支払_締め日',
  '支払_支払月',
  '支払_支払日',
  '支払_土日祝',
  '入金_締め日',
  '入金_支払月',
  '入金_支払日',
  '入金_土日祝',
  '備考',
  '状態',
] as const;

const KIND_SEPARATOR = '・';

function dayCell(d: DayOrEnd): string {
  return d === 'end' ? '末' : String(d);
}

function termsCells(t: PaymentTerms | null | undefined): string[] {
  if (!t) return ['', '', '', ''];
  return [
    dayCell(t.closing_day),
    MONTH_OFFSET_OPTIONS.find((o) => o.value === t.month_offset)?.label ?? String(t.month_offset),
    dayCell(t.pay_day),
    t.holiday_shift === 'before' ? '前倒し' : t.holiday_shift === 'after' ? '後ろ倒し' : 'ずらさない',
  ];
}

/** 書き出しの行(見出し行を含む) */
export function partnerRows(vendors: Vendor[]): string[][] {
  const rows: string[][] = [[...PARTNER_CSV_HEADERS]];
  for (const v of vendors) {
    rows.push([
      v.code,
      v.name,
      v.legal_name ?? '',
      kindsOf(v)
        .map((k) => PARTNER_KIND_OPTIONS.find((o) => o.value === k)?.label ?? k)
        .join(KIND_SEPARATOR),
      v.invoice_addressee ?? '',
      v.postal_code ?? '',
      v.address ?? '',
      v.invoice_reg_no ?? '',
      v.bank_info ?? '',
      ...termsCells(v.pay_terms),
      ...termsCells(v.collect_terms),
      v.note ?? '',
      v.active ? '有効' : '無効',
    ]);
  }
  return rows;
}

/** シートにそのまま貼れる形(タブ区切り)。セル内の改行・タブは空白にする */
export function toTsv(rows: string[][]): string {
  return rows.map((r) => r.map((c) => c.replace(/[\t\r\n]+/g, ' ')).join('\t')).join('\n');
}

/** CSV(Excel で文字化けしないよう先頭に BOM を付ける) */
export function toCsv(rows: string[][]): string {
  const quote = (c: string) => (/[",\r\n]/.test(c) ? `"${c.replace(/"/g, '""')}"` : c);
  return '﻿' + rows.map((r) => r.map(quote).join(',')).join('\r\n') + '\r\n';
}

/** 貼り付けられた文字列。最初の空でない行がタブを含めばタブ区切り、なければ CSV として読む(先頭の空行は読み飛ばす) */
export function parsePastedTable(text: string, parseCsv: (t: string) => CsvTable): CsvTable {
  const clean = text.replace(/^﻿/, '').replace(/^(?:[ \t]*\r?\n)+/, '');
  const firstLine = clean.split(/\r?\n/, 1)[0] ?? '';
  if (!firstLine.includes('\t')) return parseCsv(clean);
  const lines = clean.split(/\r?\n/).filter((l) => l.trim() !== '');
  const [head, ...rest] = lines.map((l) => l.split('\t'));
  return { headers: (head ?? []).map((h) => h.trim()), rows: rest };
}

// --- 取り込み ---------------------------------------------------------------

export type ImportStatus = 'new' | 'duplicate' | 'error';

export type ImportRow = {
  /** 行番号(見出しを1行目とし、空の行は数えない) */
  line: number;
  status: ImportStatus;
  message: string;
  input: PartnerInput | null;
};

function parseDay(raw: string): DayOrEnd | null {
  const s = raw.normalize('NFKC').trim().replace(/日$/, '');
  if (s === '末' || s === '末日' || s.toLowerCase() === 'end') return 'end';
  const n = Number(s);
  return Number.isInteger(n) && n >= 1 && n <= 31 ? n : null;
}

function parseMonthOffset(raw: string): PaymentTerms['month_offset'] | null {
  const s = raw.normalize('NFKC').trim();
  if (s === '') return null; // 空は「当月」にしない(Number('') は 0 になるため)
  const byLabel = MONTH_OFFSET_OPTIONS.find((o) => o.label === s);
  if (byLabel) return byLabel.value;
  const n = Number(s);
  return n === 0 || n === 1 || n === 2 || n === 3 ? n : null;
}

function parseShift(raw: string): HolidayShift | null {
  const s = raw.normalize('NFKC').trim();
  if (s === '') return 'after';
  if (s.includes('前')) return 'before';
  if (s.includes('後')) return 'after';
  if (s.includes('ずらさない') || s === 'なし') return 'none';
  const byLabel = HOLIDAY_SHIFT_OPTIONS.find((o) => o.label === s);
  return byLabel ? byLabel.value : null;
}

/** 4つのセルから支払条件。すべて空なら null(未設定)。形が違えばエラー文 */
function parseTerms(cells: string[], label: string): { terms: PaymentTerms | null; error: string | null } {
  if (cells.every((c) => c.trim() === '')) return { terms: null, error: null };
  const [c, m, p, h] = cells;
  const closing = parseDay(c ?? '');
  const offset = parseMonthOffset(m ?? '');
  const pay = parseDay(p ?? '');
  const shift = parseShift(h ?? '');
  if (closing === null || offset === null || pay === null || shift === null) {
    return { terms: null, error: `${label}の書き方が違います(例: 末・翌々月・5・後ろ倒し)` };
  }
  const terms: PaymentTerms = { closing_day: closing, month_offset: offset, pay_day: pay, holiday_shift: shift };
  const invalid = validateTerms(terms);
  return invalid ? { terms: null, error: `${label}: ${invalid}` } : { terms, error: null };
}

function parseKinds(raw: string): { kinds: PartnerKind[]; error: string | null } {
  const parts = raw
    .normalize('NFKC')
    .split(/[・,、/／\s]+/)
    .map((s) => s.trim())
    .filter((s) => s !== '');
  if (parts.length === 0) return { kinds: ['supplier'], error: null };
  const kinds = new Set<PartnerKind>();
  for (const p of parts) {
    if (p.includes('元請')) kinds.add('prime');
    else if (p.includes('売上')) kinds.add('customer');
    else if (p.includes('下請')) kinds.add('subcontractor');
    else if (p.includes('仕入') || p.includes('支払')) kinds.add('supplier');
    else return { kinds: [], error: `区分「${p}」は使えません(元請け・売上先・下請け・仕入先・支払先)` };
  }
  return { kinds: [...kinds], error: null };
}

/**
 * 取り込む表を確かめる。見出しは書き出しと同じ名前で探す(順番は問わない。「名称」は必須)。
 * - 既存の取引先と名称(全角/半角・空白を無視)またはインボイス登録番号が同じ → 重複(登録しない)
 * - ファイルの中で同じ名称・同じインボイス登録番号が2回以上 → 2回目以降は重複
 */
export function checkImport(table: CsvTable, existing: Pick<Vendor, 'name' | 'invoice_reg_no'>[]): ImportRow[] {
  const col = (name: string) => table.headers.findIndex((h) => h.normalize('NFKC').trim() === name);
  const idx = Object.fromEntries(PARTNER_CSV_HEADERS.map((h) => [h, col(h)])) as Record<
    (typeof PARTNER_CSV_HEADERS)[number],
    number
  >;
  if (idx['名称'] < 0) {
    return [{ line: 1, status: 'error', message: '見出しに「名称」の列がありません', input: null }];
  }
  const existingNames = new Set(existing.map((v) => nameKey(v.name)));
  const existingRegNos = new Set(existing.map((v) => v.invoice_reg_no).filter((x): x is string => !!x));
  const seen = new Set<string>();
  const seenRegNos = new Set<string>();

  return table.rows
    .map((cells, i) => ({ cells, line: i + 2 }))
    .filter(({ cells }) => cells.some((c) => c.trim() !== ''))
    .map(({ cells, line }): ImportRow => {
      const get = (h: (typeof PARTNER_CSV_HEADERS)[number]) => (idx[h] >= 0 ? (cells[idx[h]] ?? '').trim() : '');
      const name = get('名称');
      if (!name) return { line, status: 'error', message: '名称が空です', input: null };

      const errors: string[] = [];
      const { kinds, error: kindError } = parseKinds(get('区分'));
      if (kindError) errors.push(kindError);
      const regRaw = get('インボイス登録番号');
      const invoiceRegNo = regRaw ? normalizeInvoiceRegNo(regRaw) : null;
      if (regRaw && !invoiceRegNo) errors.push('インボイス登録番号は T+13桁 で書いてください');
      const pay = parseTerms([get('支払_締め日'), get('支払_支払月'), get('支払_支払日'), get('支払_土日祝')], '支払条件');
      const collect = parseTerms([get('入金_締め日'), get('入金_支払月'), get('入金_支払日'), get('入金_土日祝')], '入金条件');
      if (pay.error) errors.push(pay.error);
      if (collect.error) errors.push(collect.error);
      if (errors.length > 0) return { line, status: 'error', message: errors.join(' / '), input: null };

      const input: PartnerInput = {
        name,
        legalName: get('正式名称'),
        kinds,
        invoiceAddressee: get('請求書の宛名'),
        postalCode: get('郵便番号'),
        address: get('住所'),
        invoiceRegNo: invoiceRegNo ?? '',
        bankInfo: get('振込先'),
        payTerms: pay.terms,
        collectTerms: collect.terms,
        note: get('備考'),
      };
      const key = nameKey(name);
      if (existingNames.has(key)) return { line, status: 'duplicate', message: '同じ名称の取引先が登録済み', input };
      if (invoiceRegNo && existingRegNos.has(invoiceRegNo)) {
        return { line, status: 'duplicate', message: '同じインボイス登録番号の取引先が登録済み', input };
      }
      if (seen.has(key)) return { line, status: 'duplicate', message: 'ファイルの中で同じ名称が重複', input };
      if (invoiceRegNo && seenRegNos.has(invoiceRegNo)) {
        return { line, status: 'duplicate', message: 'ファイルの中で同じインボイス登録番号が重複', input };
      }
      seen.add(key);
      if (invoiceRegNo) seenRegNos.add(invoiceRegNo);
      return { line, status: 'new', message: '新規', input };
    });
}
